import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getAllUoMs,
  createUoM,
  updateUoM,
  deleteUoM,
} from "../services/kpiService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import ConfirmModal from "../components/ui/ConfirmModal";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";

const UoMManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  // State
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [selectedUoM, setSelectedUoM] = useState(null);
  const [formData, setFormData] = useState({
    uomValue: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Delete confirmation
  const [confirmId, setConfirmId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Dropdown
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllUoMs();
      setUoms(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load Units of Measure");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered UoMs
  const filteredUoMs = useMemo(() => {
    return uoms.filter((uom) => {
      return (
        (uom.uomValue || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (uom.description || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [uoms, searchTerm]);

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      uomValue: "",
      description: "",
    });
    setFormError(null);
    setModalMode("create");
    setSelectedUoM(null);
    setShowModal(true);
  };

  const openEditModal = (uom) => {
    setFormData({
      uomValue: uom.uomValue || "",
      description: uom.description || "",
    });
    setFormError(null);
    setModalMode("edit");
    setSelectedUoM(uom);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUoM(null);
    setFormError(null);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.uomValue.trim()) {
      setFormError("UoM Value is required");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (modalMode === "create") {
        await createUoM(formData);
        toast.success("Unit of Measure created successfully");
      } else if (modalMode === "edit" && selectedUoM) {
        await updateUoM(selectedUoM.id, formData);
        toast.success("Unit of Measure updated successfully");
      }
      await loadData();
      closeModal();
    } catch (err) {
      toast.error(err.message || "Failed to save Unit of Measure");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setConfirmBusy(true);
    try {
      await deleteUoM(confirmId);
      toast.success("Unit of Measure deleted successfully");
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to delete Unit of Measure");
    } finally {
      setConfirmBusy(false);
      setConfirmId(null);
    }
  };

  const confirmUomValue = uoms.find((u) => u.id === confirmId)?.uomValue || "";

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Units of Measure</h2>
            <p className="page-subtitle">
              Manage units of measure for your KPIs (e.g., %, hours, kg)
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search units of measure..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="secondary-btn sm" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            </div>
            <button className="primary-btn" onClick={openCreateModal}>
              + Create UoM
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading Units of Measure...</div>
        ) : (
          <div className="roles-table kpi-admin-table uom-management-table">
            <div className="roles-table-header">
              <div className="cell">ID</div>
              <div className="cell">UoM Value</div>
              <div className="cell">Description</div>
              <div className="cell actions">Actions</div>
            </div>
            {filteredUoMs.length === 0 ? (
              <div className="no-results">
                {uoms.length === 0 ? (
                  <>
                    <p>No units of measure yet.</p>
                    <button className="primary-btn" onClick={openCreateModal} style={{ marginTop: 8 }}>
                      Create your first UoM
                    </button>
                  </>
                ) : (
                  "No units of measure match your search."
                )}
              </div>
            ) : (
              filteredUoMs.map((uom) => (
                <div
                  key={uom.id}
                  className="roles-table-row"
                >
                  <div className="cell">{uom.id}</div>
                  <div className="cell">
                    <span className="uom-badge">{uom.uomValue}</span>
                  </div>
                  <div className="cell description-cell">
                    {uom.description || <span className="empty-text">-</span>}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === uom.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === uom.id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(uom.id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === uom.id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openEditModal(uom);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              setConfirmId(uom.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === "create" ? "Create New UoM" : "Edit UoM"}</h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {formError && <div className="form-error">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="uomValue">UoM Value *</label>
                  <input
                    type="text"
                    id="uomValue"
                    name="uomValue"
                    value={formData.uomValue}
                    onChange={handleInputChange}
                    placeholder="e.g., %, hours, kg, units"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description (optional)"
                    rows={3}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={saving}
                  >
                    {saving ?
                      "Saving..."
                    : modalMode === "create" ?
                      "Create"
                    : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmId}
        title="Delete Unit of Measure"
        message={`Are you sure you want to delete "${confirmUomValue}"? This may affect KPIs using this unit.`}
        confirmLabel={confirmBusy ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        isDanger
        onConfirm={handleDelete}
        onCancel={() => setConfirmId(null)}
        busy={confirmBusy}
      />
    </div>
  );
};

export default UoMManagementPage;

